import React, { PureComponent, forwardRef } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { useHistory } from 'react-router';

import cls from 'classnames/bind';
import s from './OfferCard.scss';
import {
  COLORS_MAP,
  DARK_COLORS,
  CARD_ACTIONS,
  DEFAULT_COLOR,
  OFFERS_TYPES,
  MAIN_LOCATOR,
  TARGET_BLANK_SLUG,
  TARGET_SELF_SLUG,
} from './constants/common';

import copyToClipboard from './helpers/copyToClipboard';

import { l10n } from '@ott/l10n';

import Timer from '@ott/timer';
import LoadingDots from '@ott/loading-dots';
import TimerIcon from '@ott/icon-timer';
import Tooltip from '@ott/tooltip';
import Animation, { ANIMATION_TYPES } from '@ott/animation';

const cx = cls.bind(s);

class OfferCard extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    offerMeta: PropTypes.shape({
      cardColor: PropTypes.string,
      titleColor: PropTypes.string,
      textColor: PropTypes.string,
      footerTextColor: PropTypes.string,
      isBig: PropTypes.bool,
      hideImage: PropTypes.bool,
      disableDimming: PropTypes.bool,
    }).isRequired,
    offerData: PropTypes.shape({
      buttonLink: PropTypes.string,
      buttonText: PropTypes.string,
      color: PropTypes.oneOf(['white', 'yellow', 'blue']),
      description: PropTypes.string,
      icon: PropTypes.string,
      id: PropTypes.string,
      image: PropTypes.string,
      bigImage: PropTypes.string,
      mobileImage: PropTypes.string,
      name: PropTypes.string,
      promo: PropTypes.string,
      promoType: PropTypes.string,
      title: PropTypes.string,
      type: PropTypes.string,
      specificLocators: PropTypes.array,
    }).isRequired,
    additionalOfferData: PropTypes.object,
    getAdditionalOfferData: PropTypes.func.isRequired,
    metrics: PropTypes.shape({
      click: PropTypes.func,
    }),
    actions: PropTypes.objectOf(PropTypes.func).isRequired,
    isMobileLayout: PropTypes.boolean,
  };

  static defaultProps = {
    additionalOfferData: {},
    hideImage: false,
    metrics: {
      click: noop,
    },
    offerData: {
      specificLocators: [],
    },
  };

  additionalCardData = null;
  rootRef = null;

  setRef = (elRef) => (this.rootRef = elRef);

  constructor(props, ctx) {
    super(props, ctx);

    this.state = {
      isDataCopied: false,
      isImpossibleToCopyData: false,
      metaStyles: this.getMetaStyles(props),
    };
  }

  componentDidUpdate(prevProps) {
    const {
      offerData: { id },
      additionalOfferData: { data: currentData = {} },
    } = this.props;
    const {
      additionalOfferData: { data: prevData = {} },
    } = prevProps;

    const currentAdditionalDataItem = currentData[id];
    const prevAdditionalDataItem = prevData[id];
    if (prevAdditionalDataItem && currentAdditionalDataItem) {
      if (prevAdditionalDataItem.loading && currentAdditionalDataItem.success) {
        this.copyToClipboard();
      }
    }
  }

  getOfferCardClassNames = () => {
    const { offerData, offerMeta } = this.props;

    let classNames = [];

    if (offerData.icon && (!offerData.image || offerMeta.hideImage)) {
      classNames.push('offer--withIcon');
    } else if (offerData.image && !offerMeta.hideImage) {
      classNames.push('offer--withImage');
    }

    const colorClass = this.getOfferCardClassFromColorName(
      offerMeta.cardColor || offerData.color || DEFAULT_COLOR
    );

    if (colorClass && (!offerData.image || offerMeta.hideImage)) {
      classNames.push(colorClass);
    }

    if (offerData.type === OFFERS_TYPES.info) {
      classNames.push('offer--info');
    }

    classNames.push(offerMeta.isBig ? 'offer--big' : 'offer--small');
    return classNames;
  };

  getOfferCardClassFromColorName = (color) => {
    return COLORS_MAP[color];
  };

  getMetaStyles = (props) => {
    const {
      offerMeta: { textColor, titleColor, footerTextColor },
    } = props;

    return {
      ...(textColor && { textColor: { color: textColor } }),
      ...(titleColor && { titleColor: { color: titleColor } }),
      ...(footerTextColor && { footerTextColor: { color: footerTextColor } }),
    };
  };

  getHeaderContent = () => {
    const {
      offerData: { title, icon, image, timerTo },
      offerMeta: { hideImage },
    } = this.props;

    if (icon && (!image || hideImage)) {
      return <img className={cx('offer__icon')} src={icon} alt={title} />;
    }

    if (timerTo) {
      let time = new Date(timerTo) - new Date();
      if (time < 0) {
        time = 0;
      }

      const day = Math.floor(time / (1000 * 60 * 60 * 24));

      if (day < 1) {
        return (
          <Timer
            className={cx('offer__timer')}
            childrenClassNames={{
              dayClass: cx('timer__day'),
            }}
            to={new Date(timerTo)}
          />
        );
      }

      return (
        <span className={cx('offer__countdown')}>
          <TimerIcon className={cx('countdown__icon')} />
          <span className={cx('countdown__days')}>
            {l10n('offers.daysLeft', { days: day + 1 })}
          </span>
        </span>
      );
    }

    return null;
  };

  getActionContent = () => {
    const {
      offerData: { type, buttonText, promo },
    } = this.props;

    const { metaStyles } = this.state;

    switch (type) {
      case OFFERS_TYPES.info:
        return null;
      case OFFERS_TYPES.personal_promo:
      case OFFERS_TYPES.referral_promo:
      case OFFERS_TYPES.referral_link:
      case OFFERS_TYPES.dynamic_link:
        return this.renderDataActionArea();
      case OFFERS_TYPES.common_promo:
        return this.renderCopyDataArea(promo);
      case OFFERS_TYPES.direct_discount:
      case OFFERS_TYPES.common_discount:
      default:
        return (
          <div key={type} className={cx('offer__action')}>
            <span className={cx('action__text')} style={metaStyles.footerTextColor}>
              {buttonText}
            </span>
          </div>
        );
    }
  };

  renderActionContainer = () => {
    const {
      offerData: { type },
    } = this.props;

    if (type === OFFERS_TYPES.info) {
      return null;
    }

    return (
      <div className={cx('offer__actionContainer')}>
        <Animation type={ANIMATION_TYPES.OPACITY} appear leave={false}>
          {this.getActionContent()}
        </Animation>
      </div>
    );
  };

  getDisplayWhiteDots = () => {
    const {
      offerData: { image },
      offerMeta: { hideImage },
    } = this.props;

    return (!hideImage && image) || this.isDarkBackground();
  };

  renderDataActionArea = () => {
    const {
      offerData: { promoType, id },
      additionalOfferData: { data = {} },
    } = this.props;

    const { metaStyles } = this.state;

    const additionalDataItem = data[id];

    if (additionalDataItem && !additionalDataItem.error) {
      const { loading, data } = additionalDataItem;
      if (loading) {
        return (
          <div
            className={cx(
              'offer__action',
              'offer__action--promo',
              'offer__action--personalPromo',
              'offer__action--loadingPromo'
            )}
          >
            <LoadingDots isWhite={this.getDisplayWhiteDots()} />
          </div>
        );
      } else if (data && data.result) {
        return this.renderCopyDataArea(additionalDataItem.data.result);
      }
    }

    const buttonText = this.getValueTextObject().copy;
    return (
      <div
        key={promoType}
        className={cx('offer__action', 'offer__action--promo', 'offer__action--personalPromo')}
        style={{
          ...metaStyles.footerTextColor,
        }}
      >
        {buttonText}
      </div>
    );
  };

  renderCopyDataArea = (value) => {
    const { isDataCopied, isImpossibleToCopyData, metaStyles } = this.state;
    const {
      offerData: { type: offerType },
    } = this.props;
    const areaText = this.getValueTextObject();

    return (
      <Tooltip
        content={isImpossibleToCopyData ? areaText.cantBeCopied : areaText.copied}
        isVisible={isDataCopied || isImpossibleToCopyData}
        key={offerType + '_area'}
        triggerClassName={cx('offer__trigger')}
      >
        <div
          className={cx('offer__action', this.getCopyAreaClass())}
          style={{
            ...metaStyles.footerTextColor,
          }}
        >
          {this.renderCopyValueElement(value)}
        </div>
      </Tooltip>
    );
  };

  renderCopyValueElement = (value) => {
    const {
      offerData: { type: offerType },
    } = this.props;
    if (offerType === OFFERS_TYPES.referral_link || offerType === OFFERS_TYPES.dynamic_link) {
      return <input className={cx('action__value')} value={value} />;
    }
    return <span className={cx('action__value')}>{value}</span>;
  };

  refreshPromocodeArea = () => {
    setTimeout(
      () =>
        this.setState({
          isDataCopied: false,
          isImpossibleToCopyData: false,
        }),
      3000
    );
  };

  copyToClipboard = (evt) => {
    const { isDataCopied } = this.state;
    const data = this.additionalCardData || this.props.promo;
    if (!isDataCopied) {
      try {
        copyToClipboard(data);
        this.setState(
          {
            isDataCopied: true,
          },
          this.refreshPromocodeArea
        );
      } catch (error) {
        this.setState(
          {
            isImpossibleToCopyData: true,
          },
          this.refreshPromocodeArea
        );
      }
    }

    this.handleClickBase(evt);
  };

  generateAdditionalData = (evt) => {
    const {
      offerData: { id, type },
      getAdditionalOfferData,
    } = this.props;

    this.handleClickBase(evt);
    getAdditionalOfferData(id, type);
  };

  handleClickBase = (evt) => {
    const {
      offerData: { id: offer_id, name: offer_name },
      offerMeta: { metricsKM },
      metrics,
      history,
    } = this.props;

    if (evt) {
      evt.preventDefault();
    }

    metrics.click(
      {
        offer_id,
        offer_name,
      },
      metricsKM
    );

    const cardAction = this.getCardAction();

    if (cardAction.type === CARD_ACTIONS.LINK) {
      if (cardAction.target === TARGET_BLANK_SLUG) {
        // Open in new page
        window.open(cardAction.href);
      } else {
        const { origin, pathname, search } = new URL(cardAction.href);
        const { origin: windowOrigin, pathname: windowPathname } = window.location;

        if (origin + pathname === windowOrigin + windowPathname) {
          // Open in same page without page reload
          const newUrl = pathname + search;
          history.replace(newUrl);
        } else {
          // Open in same page with page reload
          window.location.href = cardAction.href;
        }
      }
    }
  };

  getValueTextObject = () => {
    const {
      offerData: { type: offerType },
    } = this.props;
    switch (offerType) {
      case OFFERS_TYPES.personal_promo:
      case OFFERS_TYPES.referral_promo:
      case OFFERS_TYPES.common_promo:
        return {
          copy: l10n('offers.buttons.promocode'),
          copied: l10n('offers.promocodeMessages.copied'),
          cantBeCopied: l10n('offers.promocodeMessages.cantBeCopied'),
        };
      case OFFERS_TYPES.referral_link:
      case OFFERS_TYPES.dynamic_link:
        return {
          copy: l10n('offers.buttons.link'),
          copied: l10n('offers.linkMessages.copied'),
          cantBeCopied: l10n('offers.linkMessages.cantBeCopied'),
        };
    }

    return {};
  };

  getCopyAreaClass = () => {
    const {
      offerData: { type: offerType },
    } = this.props;
    switch (offerType) {
      case OFFERS_TYPES.personal_promo:
      case OFFERS_TYPES.referral_promo:
      case OFFERS_TYPES.common_promo:
        return 'offer__action--promo';
      case OFFERS_TYPES.referral_link:
      case OFFERS_TYPES.dynamic_link:
        return 'offer__action--link';
    }

    return '';
  };

  isHeadElementEmpty = () => {
    const {
      offerData: { icon, timerTo },
    } = this.props;
    return !icon && !timerTo;
  };

  isDarkBackground = () => {
    const {
      offerData: { color: dataColor },
      offerMeta: { cardColor: metaColor },
    } = this.props;

    return DARK_COLORS.findIndex((c) => c === dataColor || c === metaColor) >= 0;
  };

  isActionLink = (link) => {
    return link && link.startsWith('action://');
  };

  getActionFromDeeplink = (link) => {
    return link.match(/action:\/\/(.+)/i)[1];
  };

  getCardAction = () => {
    const {
      offerData: { buttonLink, openLinkInSameTab, id, type },
      offerData,
      offerMeta: { behavior = [] },
      offerMeta,
      additionalOfferData: { data = {} },
      actions,
    } = this.props;
    const { isDataCopied, isImpossibleToCopyData } = this.state;

    if (behavior.length || this.isActionLink(buttonLink)) {
      const offerActions = behavior.length
        ? behavior.filter((action) => Boolean(actions[action]))
        : [this.getActionFromDeeplink(buttonLink)];

      return {
        type: CARD_ACTIONS.NO_ACTION,
        onClick: (evt) => {
          this.handleClickBase(evt);
          offerActions.forEach((action) => {
            actions[action]({
              offerData,
              offerMeta,
            });
          });
        },
      };
    }

    const additionalDataItem = data[id];
    const typeIsInfo = type === OFFERS_TYPES.info;

    switch (true) {
      case Boolean(additionalDataItem && !additionalDataItem.error): {
        const { data, loading } = additionalDataItem;
        if (loading) {
          return { type: CARD_ACTIONS.NO_ACTION, onClick: this.handleClickBase };
        }
        this.additionalCardData = data.result;
        return {
          type: CARD_ACTIONS.COPY_DATA,
          onClick: this.copyToClipboard,
        };
      }

      case Boolean(buttonLink && type !== OFFERS_TYPES.dynamic_link): {
        return {
          type: CARD_ACTIONS.LINK,
          href: buttonLink,
          target: openLinkInSameTab ? TARGET_SELF_SLUG : TARGET_BLANK_SLUG,
          onClick: this.handleClickBase,
        };
      }

      case isDataCopied:
      case isImpossibleToCopyData:
      case typeIsInfo: {
        return {
          type: CARD_ACTIONS.NO_ACTION,
          onClick: this.handleClickBase,
        };
      }

      case [
        OFFERS_TYPES.personal_promo,
        OFFERS_TYPES.referral_promo,
        OFFERS_TYPES.referral_link,
        OFFERS_TYPES.dynamic_link,
      ].some((offerType) => offerType === type): {
        return {
          type: CARD_ACTIONS.FETCH_DATA,
          onClick: this.generateAdditionalData,
        };
      }
    }

    return {
      type: CARD_ACTIONS.COPY_DATA,
      onClick: this.copyToClipboard,
    };
  };

  getOfferImage = () => {
    const {
      offerMeta: { isBig },
      offerData: { image, bigImage, mobileImage },
      isMobileLayout,
    } = this.props;

    if (isBig && bigImage && !isMobileLayout) {
      return bigImage;
    }

    if (isMobileLayout && mobileImage) {
      return mobileImage;
    }

    return image;
  };

  render() {
    const {
      className,
      offerMeta: { hideImage, disableDimming },
      offerData: { image, title, description, id, specificLocators },
      isActive,
    } = this.props;
    const { metaStyles } = this.state;

    const imageStyles =
      image && isActive && !hideImage
        ? {
            backgroundImage: `url(${this.getOfferImage()})`,
          }
        : {};
    const { type, ...cardAction } = this.getCardAction();

    const classNames = this.getOfferCardClassNames();

    return (
      <div
        className={className}
        ref={this.setRef}
        data-locator={cls(MAIN_LOCATOR, specificLocators)}
        key={`${id}_container`}
      >
        <a
          key={`${id}_card`}
          className={cx('offer', ...classNames)}
          style={{ ...imageStyles }}
          {...cardAction}
        >
          <div className={cx('offer__overlay')} />
          <div
            className={cx('offer__content', { ['offer__content--withoutDimming']: disableDimming })}
          >
            <div className={cx('offer__headerPart')}>{this.getHeaderContent()}</div>
            <div className={cx('offer__contentPart')}>
              <div className={cx('offer__text')}>
                <div
                  className={cx('offer__title')}
                  style={metaStyles.titleColor}
                  dangerouslySetInnerHTML={{ __html: title }}
                />
                <div
                  className={cx('offer__description')}
                  style={metaStyles.textColor}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </div>
              {this.renderActionContainer()}
            </div>
          </div>
        </a>
      </div>
    );
  }
}

const RefWrapper = forwardRef((props, ref) => {
  const history = useHistory();
  return <OfferCard ref={ref} {...props} history={history} />;
});

export default RefWrapper;
